import SaveToShortlistButton from '@bridebook/web/app-basic/components/save-to-shortlist-button';
import { CarouselCounter } from '@bridebook/web/app-basic/components/supplier/sections/carousel/carousel-counter';
import { CarouselImage } from '@bridebook/web/app-basic/components/supplier/sections/carousel/carousel-image';
import { viewedSupplierMedia } from '@bridebook/web/app-basic/lib/analytics';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import {
  getAllSupplierPhotos,
  getIsVenue,
  getPremium,
  getSupplier,
  getSupplierVideos,
} from '@bridebook/web/app-basic/lib/supplier/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import AliceCarousel, { EventObject } from 'react-alice-carousel';
import { useDispatch } from 'react-redux';
import { IVideo } from '@bridebook/models/source/models/Suppliers/Videos.types';
import { Box, ButtonChevron } from '@bridebook/ui';
import Swipeable from '@bridebook/ui/src/components/bbcommon/swipeable/swipeable';
import VideoEmbed from '@bridebook/web/app-shared/components/video-embed/video-embed';
// eslint-disable-next-line no-restricted-imports
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import styles from './gallery.style';
import { getVideoIframeUrl } from '@bridebook/web/app-shared/lib/utils/url';

export interface IGalleryConfig {
  maxSlides: number;
  innerCtaIndex: number;
  minSlidesForInnerCta: number;
  analyticsIndexes: number[];
  includeVideos: boolean;
  ctaSize: 'large' | 'small';
  roundCorners: boolean;
}

const configFactory = (isVenue: boolean, placement?: 'top' | 'accordion'): IGalleryConfig => ({
  maxSlides: isVenue ? 1 : 10,
  // starting from 0, number indicates in where to set the cta cover
  innerCtaIndex: 2,
  minSlidesForInnerCta: 4,
  // starting from 0
  analyticsIndexes: [4, 9],
  includeVideos: false,
  ctaSize: 'large',
  roundCorners: false,
  ...(placement === 'accordion' && {
    maxSlides: 30,
    ctaSize: 'small',
    roundCorners: true,
    includeVideos: true,
  }),
});

type Photo = ReturnType<typeof getAllSupplierPhotos>[0];

const GalleryComponent = ({ placement }: { placement?: 'top' | 'accordion' }) => {
  const { t } = useTranslation('supplier');
  const [activeIndex, setActiveIndex] = useState(0);
  const carousel = useRef<AliceCarousel>(null);
  const dispatch = useDispatch();
  const router = useRouter();
  const isMobile = useSelector(getIsMobile);
  const isVenue = useSelector(getIsVenue);

  const [showCtaLayer, setShowCtaLayer] = useState(false);
  const [carouselReady, setCarouselReady] = useState(false);
  const toggleShortlisted = useCallback(() => router.push(UrlHelper.signup), [router]);

  const config = useMemo(
    () => ({
      ...configFactory(isVenue, placement),
    }),
    [isVenue, placement],
  );

  // get supplier media
  const photos = useSelector(getAllSupplierPhotos).slice(0, config.maxSlides);
  const videos = useSelector(getSupplierVideos);
  const lastPhoto = photos.pop();
  const slides = useMemo(
    () => [...photos, lastPhoto, ...(placement === 'accordion' ? videos : []).filter(Boolean)],
    [lastPhoto, photos, placement, videos],
  ) as (Photo | IVideo)[];

  const showPrevNavButton =
    !!activeIndex && carouselReady && (!isMobile || (isMobile && placement === 'accordion'));
  const showNextNavButton =
    activeIndex < slides.length - 1 &&
    carouselReady &&
    (!isMobile || (isMobile && placement === 'accordion'));

  const supplier = useSelector(getSupplier);
  const premium = useSelector(getPremium);

  const onSlideChangedCb = useCallback(
    (e: EventObject) => {
      const slideIndex = e.item;
      // @ts-expect-error no other way to access the root node
      const rootEl: HTMLUListElement | undefined = carousel.current?.rootElement;

      if (!rootEl) return;

      const isCurrentElImage =
        rootEl.querySelectorAll('img, iframe')[slideIndex]?.tagName === 'IMG';
      const nextElement = rootEl.querySelectorAll('img, iframe')[slideIndex + 1];
      const isInnerCTASlide = slideIndex === config.innerCtaIndex;
      const isLastCTASlide = slideIndex === slides.length - 1;
      // set next element to eager to preload it
      nextElement && nextElement.setAttribute('loading', 'eager');
      setActiveIndex(slideIndex);

      // show favourites button, counter, etc. on each slide except of cta
      setShowCtaLayer((isInnerCTASlide || isLastCTASlide) && isCurrentElImage && slides.length > 1);

      if (isInnerCTASlide || isLastCTASlide || config?.analyticsIndexes?.includes(slideIndex)) {
        dispatch(
          viewedSupplierMedia({
            viewedSupplierCategory: supplier?.type?.[0],
            viewedSupplierId: supplier?.id,
            viewedSupplierName: supplier?.name,
            mediaType: isCurrentElImage ? 'photo' : 'video',
            mediaCarouselTotalItems: slides.length,
            mediaCarouselCurrentItemNumber: slideIndex + 1,
            ctaPhoto: isInnerCTASlide || isLastCTASlide,
            supplierTier: premium?.tier,
          }),
        );
      }
    },
    [
      config.analyticsIndexes,
      config.innerCtaIndex,
      dispatch,
      premium?.tier,
      slides.length,
      supplier?.id,
      supplier?.name,
      supplier?.type,
    ],
  );

  const onCarouselInitialization = useCallback(
    (e: EventObject) => {
      setCarouselReady(true);
      onSlideChangedCb(e);
    },
    [onSlideChangedCb],
  );

  const handlePrevNavButtonClick = useCallback(() => {
    carousel.current?.slidePrev();
  }, []);

  const handleNextNavButtonClick = useCallback(() => {
    carousel.current?.slideNext();
  }, []);

  if (!supplier) return null;

  return (
    <Box style={styles.wrapper(config.roundCorners)}>
      <Swipeable
        onSwipedLeft={() => {
          carousel.current?.slideNext();
        }}
        onSwipedRight={() => {
          carousel.current?.slidePrev();
        }}
        preventScrollOnSwipe>
        <AliceCarousel
          touchTracking={false}
          activeIndex={activeIndex}
          autoWidth
          animationDuration={250}
          keyboardNavigation
          disableDotsControls={true}
          disableButtonsControls={true}
          onInitialized={onCarouselInitialization}
          onSlideChanged={onSlideChangedCb}
          ref={carousel}
          items={
            carouselReady
              ? slides.map((slide, index) => {
                  const isLastSlide = config.includeVideos
                    ? index === slides.length - videos.length - 1
                    : index === slides.length - 1;

                  const isInnerCTASlide = index === config.innerCtaIndex;
                  const showCtaLayer = (isInnerCTASlide || isLastSlide) && slides.length > 1;

                  const imgixParams = { blur: showCtaLayer ? '100' : '0' };
                  const labelConfig = {
                    header: isLastSlide ? t('carousel.cta.last.header') : t('carousel.cta.header'),
                    desc: isLastSlide ? t('carousel.cta.last.desc') : t('carousel.cta.desc'),
                    button: isLastSlide ? t('carousel.cta.last.button') : t('carousel.cta.button'),
                    size: config.ctaSize,
                  };

                  if ((slide as IVideo)?.type === 'video') {
                    return (
                      <VideoEmbed
                        key={index}
                        videoUrl={getVideoIframeUrl(slide as IVideo, false)}
                        style={{ paddingTop: '42px' }}
                      />
                    );
                  }

                  return (
                    <CarouselImage
                      lazyLoading={index > 0}
                      highPriority={index === 0}
                      path={(slide as Photo).path}
                      key={index}
                      imgixParams={imgixParams}
                      showCTALayer={showCtaLayer}
                      labelConfig={labelConfig}
                      enhanceImg={false}
                      clickedSection="supplierMediaGallery"
                    />
                  );
                })
              : slides.map((slide, index) => (
                  <CarouselImage
                    key={index}
                    path={(slide as Photo).path}
                    lazyLoading={index > 0}
                    highPriority={index === 0}
                    clickedSection="supplierMediaGallery"
                  />
                ))
          }
        />
      </Swipeable>
      {!showCtaLayer && (
        <>
          {slides.length > 1 && (
            <CarouselCounter total={slides.length} currentIndex={activeIndex + 1} />
          )}
          <Box style={styles.shortlistWrapper(config.ctaSize)}>
            <SaveToShortlistButton
              supplierId={supplier?.id}
              toggle={toggleShortlisted}
              variantSize="standard"
            />
          </Box>
        </>
      )}
      {showPrevNavButton && (
        <ButtonChevron
          rounded
          w={32}
          h={32}
          iconWidth={13}
          direction="left"
          style={styles.prevNavButton}
          onClick={handlePrevNavButtonClick}
        />
      )}
      {showNextNavButton && (
        <ButtonChevron
          rounded
          w={32}
          h={32}
          iconWidth={13}
          direction="right"
          style={styles.nextNavButton}
          onClick={handleNextNavButtonClick}
        />
      )}
    </Box>
  );
};

export const Gallery = memo(GalleryComponent);
